import axios from 'axios';

// ─── Platform control-plane client ───────────────────────────────────────────
//
// A SEPARATE axios instance from `api`, on purpose. The tenant client attaches
// localStorage `token` to every request and bounces 401s to /login. Reusing it
// would mean a platform token could ride along on a tenant request (and vice
// versa), and an expired operator session would dump you on the firm's login
// screen. Keeping the two instances apart makes that separation structural
// rather than something each call site has to remember.

const BASE_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';

export const SUPER_TOKEN_KEY = 'superToken';

export const superApi = axios.create({ baseURL: `${BASE_URL}/super` });

superApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(SUPER_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

superApi.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(SUPER_TOKEN_KEY);
      if (window.location.pathname !== '/super/login') {
        window.location.replace('/super/login');
      }
    }
    return Promise.reject(error);
  },
);

// ── Types ────────────────────────────────────────────────────────────────────

export type TenantStatus = 'ACTIVE' | 'TRIAL' | 'SUSPENDED' | 'CANCELLED';

export interface TenantRow {
  id: number;
  name: string;
  slug: string;
  status: TenantStatus;
  plan: string;
  maxUsers: number | null;
  storageQuotaMb: number | null;
  features: Record<string, boolean>;
  trialEndsAt: string | null;
  notes: string | null;
  createdAt: string;
  usage: { users: number; staff: number; clients: number; invoices: number; tasks: number };
  lastActivityAt: string | null;
}

export interface SupportSessionRow {
  id: number;
  targetTenantId: number;
  reason: string;
  startedAt: string;
  expiresAt: string;
  revokedAt: string | null;
  superAdmin: { id: number; name: string; email: string };
  tenant: { id: number; name: string; slug: string };
}

export interface PlatformAuditRow {
  id: number;
  action: string;
  createdAt: string;
  ip: string | null;
  detail: Record<string, unknown> | null;
  superAdmin: { id: number; name: string; email: string } | null;
  tenant: { id: number; name: string; slug: string } | null;
}

// ── Endpoints ────────────────────────────────────────────────────────────────

export const superLogin = (email: string, password: string) =>
  superApi.post<{ token: string; superAdmin: { id: number; email: string; name: string } }>('/login', { email, password });

export const superMe = () => superApi.get<{ id: number; email: string; name: string }>('/me');

export const listTenants = () => superApi.get<TenantRow[]>('/tenants');

export const createTenant = (data: {
  name: string;
  slug: string;
  plan?: string;
  maxUsers?: number | null;
  storageQuotaMb?: number | null;
  trialEndsAt?: string | null;
  notes?: string | null;
  admin?: { email: string; name: string; password: string };
}) => superApi.post('/tenants', data);

export const updateTenant = (
  id: number,
  data: Partial<{
    name: string;
    plan: string;
    maxUsers: number | null;
    storageQuotaMb: number | null;
    features: Record<string, boolean>;
    trialEndsAt: string | null;
    notes: string | null;
  }>,
) => superApi.put(`/tenants/${id}`, data);

export const setTenantStatus = (id: number, status: TenantStatus, reason?: string) =>
  superApi.put(`/tenants/${id}/status`, { status, reason });

export const reprovisionTenant = (id: number) => superApi.post(`/tenants/${id}/provision`);

export const startSupportSession = (id: number, reason: string, hours: number) =>
  superApi.post<{ session: { id: number; tenantName: string; expiresAt: string }; token: string; readOnly: boolean }>(
    `/tenants/${id}/support-sessions`,
    { reason, hours },
  );

export const listSupportSessions = (tenantId?: number) =>
  superApi.get<SupportSessionRow[]>('/support-sessions', { params: tenantId ? { tenantId } : {} });

export const revokeSupportSession = (sessionId: number) => superApi.delete(`/support-sessions/${sessionId}`);

export const listPlatformAudit = (params: { tenantId?: number; action?: string; limit?: number } = {}) =>
  superApi.get<PlatformAuditRow[]>('/audit', { params });

/** Feature keys the server understands (lib/tenantPlan.ts). */
export const FEATURE_KEYS: Array<{ key: string; label: string }> = [
  { key: 'payroll', label: 'Payroll' },
  { key: 'litigation', label: 'Litigation tracking' },
  { key: 'rulesEngine', label: 'Rules engine' },
  { key: 'chat', label: 'Team chat' },
  { key: 'tenders', label: 'Tenders' },
  { key: 'leads', label: 'Leads' },
];
